import { ReactNode, useMemo, useState } from 'react'
import { ControlledProps } from '../types'
import { Frequency, Options, RRule, Weekday } from 'rrule'
import TextInput from './form/TextInput'
import RadioInput from './form/RadioInput'
import { format, parse } from 'date-fns'
import { FieldOption } from './form/Field'
import { MultiOptionField } from './form/MultiOptionField'
import Heading from './Heading'
import { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'

const getFrequencyOptions = (t: TFunction<'translation'>): FieldOption[] => [
    {
        key: 'once',
        label: t('frequency_once'),
    },
    {
        key: Frequency.DAILY,
        label: t('frequency_daily'),
    },
    {
        key: Frequency.MONTHLY,
        label: t('frequency_monthly'),
    },
]

export const getDayOptions = (t: TFunction<'translation'>): FieldOption[] => [
    {
        key: 'MO',
        label: t('day_mon'),
    },
    {
        key: 'TU',
        label: t('day_tue'),
    },
    {
        key: 'WE',
        label: t('day_wed'),
    },
    {
        key: 'TH',
        label: t('day_thu'),
    },
    {
        key: 'FR',
        label: t('day_fri'),
    },
    {
        key: 'SA',
        label: t('day_sat'),
    },
    {
        key: 'SU',
        label: t('day_sun'),
    },
]

interface RRuleEditorProps extends ControlledProps<[string, Partial<Options> | undefined]> {
    label?: ReactNode
    disabled?: boolean
}

type RuleFrequency = 'once' | Frequency
type RuleOptions = Omit<Options, 'freq'> & { freq: RuleFrequency }

function getValue<T>(val: T | T[]): T {
    return Array.isArray(val) ? val[0] : val
}

function getArray<T>(val: T | T[]): T[] {
    return Array.isArray(val) ? val : [val]
}

export const rruleToText = (options: RuleOptions, t: TFunction<'translation'>) => {
    // Examples
    // every X (day, week, month) at Y (time) until Z (date with time)
    // every day on (days) at Y (time) until Z (date with time)

    const getHour = (hour: number | number[]) => {
        const time = getValue(hour)
        return t('rrule_at_time', {
            time: time % 12 === 0 ? 12 : time % 12,
            ampm: time < 12 ? 'am' : 'pm',
        })
    }

    const parts = []
    if (!options.freq) {
        parts.push(t('rrule_once', {
            date: options.dtstart
                ? format(options.dtstart, 'PP')
                : <>&#8211;</>,
        }))

        if (options.byhour) {
            parts.push(getHour(options.byhour)) // at (time)
        }

        return parts.join(' ')
    }

    if (options.freq === RRule.DAILY) {
        parts.push(
            options.interval > 1
                ? t('rrule_every_days', { count: options.interval }) // every X days
                : t('rrule_every_day'), // every day
        )
    } else if (options.freq === RRule.MONTHLY) {
        parts.push(
            options.interval > 1
                ? t('rrule_every_months', { count: options.interval }) // every X months
                : t('rrule_every_month'), // every month
        )
    }
    if (options.byhour) {
        parts.push(getHour(options.byhour)) // at (time)
    }
    if (options.byweekday) {
        parts.push(t('rrule_on_days', { days: getArray(options.byweekday).map((d) => t(`rrule_day_of_week_${d}`)).join(', ') })) // on (days)
    }
    if (options.until) {
        parts.push(t('rrule_until', { date: format(options.until, 'PP') })) // until (date)
    }

    return parts.join(' ')
}

export default function RRuleEditor({ label, onChange, value, disabled }: RRuleEditorProps) {
    const { t } = useTranslation()
    const options = useMemo<Partial<RuleOptions>>(() => {
        const rule = value[0]
        if (rule) {
            try {
                const options = RRule.fromString(rule).origOptions as RuleOptions
                if (options.freq === undefined) {
                    options.freq = 'once'
                }
                return options
            } catch {}
        }
        return {
            freq: 'once',
        } satisfies Partial<RuleOptions>
    }, [value])
    const [startDate, setStartDate] = useState(options.dtstart ? format(options.dtstart, 'yyyy-MM-dd') : '')
    const [endDate, setEndDate] = useState(options.until ? format(options.until, 'yyyy-MM-dd') : '')
    const now = new Date(Date.now())

    const setValues = ({ freq, ...options }: Partial<RuleOptions>) => {
        const rule: Partial<Options> = {
            ...options,
            freq: freq === 'once' ? undefined : freq,
        }
        onChange([RRule.optionsToString(rule), rule])
    }

    return (
        <fieldset style={{ border: 0, padding: 0 }}>
            <legend>
                <Heading size="h4" title={label} />
            </legend>
            <p>{t('rrule_timezone_note')}</p>
            <RadioInput
                label={t('frequency')}
                required
                value={options.freq as RuleFrequency}
                options={getFrequencyOptions(t)}
                onChange={(freq: RuleFrequency) => setValues({ ...options, freq })}
                disabled={disabled}
            />
            <TextInput
                name="startDate"
                label={t(options.freq !== 'once' ? 'start_date' : 'on_date')}
                type="date"
                required
                value={startDate}
                onChange={setStartDate}
                onBlur={(event) => {
                    const value = event.target.value
                    const date = parse(value, 'yyyy-MM-dd', now)
                    setValues({ ...options, dtstart: value ? date : null })
                }}
                disabled={disabled}
            />
            { options.freq !== 'once' && (
                <>
                    <TextInput
                        name="endDate"
                        label={t('end_date')}
                        type="date"
                        value={endDate}
                        onChange={setEndDate}
                        onBlur={(event) => {
                            const value = event.target.value
                            const date = parse(value, 'yyyy-MM-dd', now)
                            setValues({ ...options, until: value ? date : null })
                        }}
                        clearable
                        disabled={disabled}
                    />
                </>
            )}
            <TextInput
                name="hour"
                label={t('hour_24hr_format')}
                type="number"
                min={1}
                max={24}
                required
                value={Number(options.byhour ?? 0)}
                onChange={byhour => setValues({ ...options, byhour })}
                disabled={disabled}
            />
            { options.freq !== 'once' && (
                <>
                    <TextInput
                        name="interval"
                        label={t('interval')}
                        type="number"
                        min={1}
                        required
                        value={options.interval ?? 1}
                        onChange={interval => setValues({ ...options, interval })}
                        disabled={disabled}
                    />
                    {
                        options.freq === Frequency.DAILY && (
                            <MultiOptionField
                                options={getDayOptions(t)}
                                value={(Array.isArray(options.byweekday) ? options.byweekday : options.byweekday ? [options.byweekday] : []).map(w => {
                                    if (w instanceof Weekday) {
                                        return w.toString()
                                    }
                                    return w
                                })}
                                onChange={byweekday => setValues({ ...options, byweekday: byweekday.map(n => Weekday.fromStr(n)) })}
                                label={t('days')}
                                disabled={disabled}
                            />
                        )
                    }
                </>
            )}
        </fieldset>
    )
}
